import type { NatProfile } from './nat';

/**
 * Choosing how two peers connect, from each side's NAT profile.
 *
 * The controller sits between two edges and knows both their NAT situations, so
 * it is the only party that can decide the method before either side wastes time
 * on one that cannot work. The decision is symmetric — it must not matter which
 * peer is called A — because both edges compute it independently from the same
 * exchanged profiles and must agree, or one dials direct while the other waits
 * for a relay.
 *
 * The order of preference is fixed: a direct path costs nothing and is fastest;
 * a punched path costs a little setup and some keepalive traffic; a relay costs
 * money and adds a hop. So the strategy takes the cheapest method both peers can
 * actually use, and never a cheaper one that only one of them can.
 */

export const CONNECTION_METHODS = [
  /** A routable path with no NAT in the way, IPv6 or a public v4 endpoint. */
  'DIRECT',
  /** Both NATs are hole-punchable; the controller coordinates a simultaneous open. */
  'HOLE_PUNCH',
  /** At least one side cannot be reached directly; traffic goes through a relay. */
  'RELAY',
] as const;
export type ConnectionMethod = (typeof CONNECTION_METHODS)[number];

export interface ConnectionPlan {
  method: ConnectionMethod;
  /** Whether persistent keepalive is needed to hold a NAT mapping open. */
  keepalive: boolean;
  /** Why this method was chosen, for an operator reading a connection page. */
  reason: string;
  /**
   * True when the chosen method is a fallback from a better one that could not
   * be used, so the console can show it as degraded rather than as normal.
   */
  degraded: boolean;
}

/**
 * Decide how two peers should connect.
 *
 * Symmetric in its arguments: swapping A and B gives the same method. Any
 * asymmetry would let the two edges disagree, and a connection where one side
 * dials and the other relays never forms.
 */
export function planConnection(a: NatProfile, b: NatProfile): ConnectionPlan {
  // A direct IPv6 path sidesteps every NAT on both sides. It is preferred over
  // everything, including a public-v4 hole-punch, because there is no NAT to
  // punch and no mapping to keep alive.
  if (a.preferIpv6 && b.preferIpv6) {
    return {
      method: 'DIRECT',
      keepalive: false,
      reason: 'Both edges have global IPv6, so they connect directly with no NAT in the way.',
      degraded: false,
    };
  }

  // One side is on a public address: the other can reach it directly, whatever
  // its own NAT, by dialling toward the public side.
  if (a.mapping === 'DIRECT' || b.mapping === 'DIRECT') {
    return {
      method: 'DIRECT',
      keepalive: false,
      reason: 'One edge has a public endpoint the other can reach directly.',
      degraded: false,
    };
  }

  // Both must be hole-punchable for a punch to work: coordinating a simultaneous
  // open needs both mappings to be predictable. One symmetric side is enough to
  // rule it out.
  if (a.holePunchable && b.holePunchable) {
    return {
      method: 'HOLE_PUNCH',
      // Keepalive holds both NAT mappings open so the punched hole does not close
      // between packets and strand the tunnel.
      keepalive: true,
      reason: 'Both edges are behind hole-punchable NATs; the controller coordinates the punch.',
      degraded: false,
    };
  }

  // Nothing direct or punchable is possible on both sides. A relay is the
  // reliable answer, and it is marked degraded so the operator sees that a better
  // path was wanted but not available.
  return {
    method: 'RELAY',
    keepalive: true,
    reason: relayReason(a, b),
    degraded: true,
  };
}

function relayReason(a: NatProfile, b: NatProfile): string {
  const blockers: string[] = [];
  if (a.cgnat || b.cgnat) blockers.push('one edge is behind carrier-grade NAT');
  if (a.mapping === 'ENDPOINT_DEPENDENT' || b.mapping === 'ENDPOINT_DEPENDENT') {
    blockers.push('one edge is behind symmetric NAT');
  }
  if (a.mapping === 'BLOCKED' || b.mapping === 'BLOCKED') blockers.push('one edge blocks UDP');
  if (a.mapping === 'UNKNOWN' || b.mapping === 'UNKNOWN') {
    blockers.push('one edge could not be classified confidently');
  }
  const detail = blockers.length > 0 ? ` (${blockers.join('; ')})` : '';
  return `A direct or hole-punched path is not possible, so traffic goes through a relay${detail}.`;
}

/** How a connection is actually running, as an operator sees it. */
export const CONNECTION_STATES = [
  'DIRECT',
  'NAT_TRAVERSED',
  'RELAYED',
  'DEGRADED',
  'FAILED',
] as const;
export type ConnectionState = (typeof CONNECTION_STATES)[number];

export interface ConnectionStatusInput {
  planned: ConnectionMethod;
  /** Whether the tunnel has a recent handshake. */
  established: boolean;
  /**
   * Whether the connection is running on a worse method than planned — a punch
   * that failed and fell back to a relay, for instance.
   */
  fellBack: boolean;
}

/**
 * Map what was planned and what is actually happening to a display state.
 *
 * Kept separate from the plan because a plan is an intention and a state is an
 * observation, and conflating them hides exactly the case an operator cares
 * about: a connection that was meant to be direct but is limping through a relay.
 */
export function connectionState(input: ConnectionStatusInput): ConnectionState {
  if (!input.established) return 'FAILED';
  if (input.fellBack) return 'DEGRADED';
  switch (input.planned) {
    case 'DIRECT':
      return 'DIRECT';
    case 'HOLE_PUNCH':
      return 'NAT_TRAVERSED';
    case 'RELAY':
      return 'RELAYED';
    default:
      return 'FAILED';
  }
}

/**
 * The best path a single edge could take, from its own NAT profile alone.
 *
 * A real connection is between two peers and its method depends on both, so this
 * is deliberately a *best case*, not a promise: a hole-punchable edge can at best
 * NAT-traverse, but only if its peer can too; an edge that needs a relay will need
 * one against every peer. It never returns DEGRADED or FAILED, which are
 * observations of a live tunnel this function cannot see. It exists so a device
 * page can show what kind of connectivity an edge is capable of before any peer is
 * chosen — the honest ceiling, labelled as such in the UI.
 */
export function natReadiness(profile: NatProfile): ConnectionState {
  if (profile.preferIpv6 || profile.mapping === 'DIRECT') return 'DIRECT';
  if (profile.holePunchable) return 'NAT_TRAVERSED';
  return 'RELAYED';
}
